import React, { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { Card, Chip, Empty } from './ui';
import { composeBriefing } from '../lib/bridge';
import { founderQueueOk } from '../lib/operatingMode';
import { isAfter } from '../lib/dates';
import { api } from '../state/api';
import { GateAccountabilityBand } from './GateAccountabilityBand';

/** Exceptions only — not a second dashboard. */
export function AttentionNeeded({ go, openModal }) {
  const { state, d, today, reload } = useStore();
  const preSprint = d.sprint.phase === 'pre';
  const [busyId, setBusyId] = useState('');
  const items = useMemo(() => {
    const rows = [];
    if (!founderQueueOk(state) && (state.accounts || []).length > 0) {
      rows.push({
        id: 'confirm-priority-queue',
        title: preSprint
          ? `Confirm the priority queue before execution begins on ${d.sprint.start || 'sprint start'}`
          : 'Confirm the priority account list',
        detail: 'Bridge needs a confirmed 10–15 priority list to generate named outreach.',
        severity: 'high',
        goto: 'command',
      });
    }
    for (const act of state.crmActivities || []) {
      if (String(act.reconcileStatus || '') !== 'needs_confirmation') continue;
      const moveIds = Array.isArray(act.candidateMoveIds) ? act.candidateMoveIds : [];
      rows.push({
        id: `crm-confirm-${act.id}`,
        title: 'CRM activity needs confirmation',
        detail: `${act.channel || act.type} on ${act.accountNexusId || 'account'} — ${act.reconcileNote || 'match is ambiguous'}.`,
        severity: 'high',
        goto: 'command',
        crmActivityId: act.id,
        candidateMoveIds: moveIds,
      });
    }
    for (const a of d.alerts || []) {
      rows.push({
        id: a.id,
        title: a.title,
        detail: a.detail,
        severity: a.severity,
        goto: a.goto || 'pipeline',
      });
    }
    for (const w of d.top3?.waitingExternal || []) {
      rows.push({
        id: `wait-${w.id}`,
        title: w.noResponseFlag ? `No response: ${w.label || w.id}` : `Waiting: ${w.label || w.id}`,
        detail: w.noResponseFlag
          ? `Response window ended ${w.waitingUntil || ''}`.trim()
          : w.waitingFor || w.waitingUntil || 'External commitment outstanding',
        severity: w.noResponseFlag ? 'high' : 'medium',
        goto: w.kind === 'account' ? 'pipeline' : 'actions',
      });
    }
    const overdueIds = new Set();
    for (const o of d.top3?.overdue || []) {
      overdueIds.add(o.id);
      rows.push({
        id: `od-${o.id}`,
        title: `Overdue: ${o.text}`,
        detail: o.dueDate || 'One planned move is overdue and needs replacement.',
        severity: 'high',
        goto: 'command',
      });
    }
    // Any Today / AI Plan move past due must surface even if priority was Medium.
    for (const t of state.top3 || []) {
      if (overdueIds.has(t.id)) continue;
      if (t.status === 'Closed' || t.status === 'Parked' || t.status === 'Superseded') continue;
      const onToday = Number(t.aiPlanRank) > 0 || t.selectedForToday || t.status === 'Today';
      if (!onToday || !t.dueDate || !d.sprint?.executionAsOf || !isAfter(d.sprint.executionAsOf, t.dueDate)) continue;
      rows.push({
        id: `od-today-${t.id}`,
        title: `Overdue: ${t.text}`,
        detail: 'One planned move is overdue and needs replacement.',
        severity: 'high',
        goto: 'command',
      });
    }
    const briefing = composeBriefing(state, d, {
      overrides: state.bridgeRecommendations || [],
    });
    for (const rec of briefing.recommendations || []) {
      if (rec.kind === 'change_primary_d001' || rec.kind === 'propose_strategy_exception') {
        if (rec.status && rec.status !== 'pending') continue;
        rows.push({
          id: rec.id,
          title: rec.kind === 'change_primary_d001' ? 'Governance: Primary D001 decision' : 'Governance: Strategy Exception',
          detail: rec.why,
          severity: 'high',
          goto: rec.kind === 'change_primary_d001' || rec.kind === 'change_primary_deal' ? 'dubai' : 'strategy',
        });
      }
    }
    const versions = [...(state.planVersions || [])].sort((a, b) =>
      String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
    );
    const plan = versions[0];
    for (const g of plan?.governanceProposals || []) {
      rows.push({
        id: `gov-${g.id || g.kind}`,
        title: `Governance: ${String(g.kind || 'proposal').replace(/_/g, ' ')}`,
        detail: g.why || g.title || '',
        severity: 'high',
        goto: 'diagnostics',
      });
    }
    return rows.slice(0, 8);
  }, [state, d, today, preSprint]);

  const confirmCrm = async (item, action) => {
    if (!item.crmActivityId) return;
    setBusyId(item.id);
    try {
      const moveId = item.candidateMoveIds?.[0];
      await api.crmConfirmActivity(item.crmActivityId, { action, moveId });
      if (typeof reload === 'function') await reload();
    } catch {
      /* keep visible */
    } finally {
      setBusyId('');
    }
  };

  return (
    <>
      <GateAccountabilityBand accountability={d.gateAccountability} go={go} compact />
      <section className="section">
      <Card title="Attention Needed" sub="Only exceptions that need the founder." flush>
        {items.length === 0 ? (
          <div style={{ padding: 14 }}>
            <Empty title="Nothing exceptional right now.">
              {preSprint
                ? 'Pre-sprint: finish launch readiness. Overdue theater stays off until execution begins.'
                : 'Execute Today’s plan. Bridge will surface overdue moves, stale CRM, runway issues, and governance here.'}
            </Empty>
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="row"
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                borderBottom: '1px solid var(--line-2)',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                className="row"
                style={{
                  flex: 1,
                  textAlign: 'left',
                  background: 'transparent',
                  border: 0,
                  cursor: 'pointer',
                  gap: 10,
                  padding: 0,
                }}
                onClick={() => !item.crmActivityId && go(item.goto)}
              >
                <Chip tone={item.severity === 'critical' || item.severity === 'high' ? 'red' : item.severity === 'medium' ? 'amber' : ''}>
                  {item.severity || 'note'}
                </Chip>
                <span style={{ flex: 1 }}>
                  <strong>{item.title}</strong>
                  {item.detail ? <span className="dim tiny" style={{ display: 'block' }}>{item.detail}</span> : null}
                </span>
              </button>
              {item.crmActivityId ? (
                <div className="row wrap" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn sm"
                    disabled={busyId === item.id || !(item.candidateMoveIds || []).length}
                    onClick={() => confirmCrm(item, 'confirm')}
                  >
                    Confirm
                  </button>
                  <button
                    type="button"
                    className="btn sm ghost"
                    disabled={busyId === item.id}
                    onClick={() => confirmCrm(item, 'reject')}
                  >
                    Dismiss
                  </button>
                </div>
              ) : null}
            </div>
          ))
        )}
        <div className="row wrap" style={{ padding: 10, gap: 8 }}>
          {!preSprint && openModal && (
            <button type="button" className="btn sm ghost" onClick={() => openModal('closeout')}>
              Close the day
            </button>
          )}
          {openModal && (
            <button type="button" className="btn sm ghost" onClick={() => openModal('evidence')}>
              Record what happened
            </button>
          )}
          <button type="button" className="btn sm ghost" onClick={() => go('strategy')}>
            Strategy decisions
          </button>
        </div>
      </Card>
    </section>
    </>
  );
}
